import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { up } from 'styled-breakpoints';
import styled, { css } from 'styled-components';
import { LggButton } from 'src/components/general/button/lgg-button';
import { BottomDrawer } from 'src/components/general/drawer/bottom/bottom-drawer';
import { LggModal } from 'src/components/general/modals/lgg-modal';
import { FlexColumn } from 'src/components/layout/flex-column';
import { FlexRow } from 'src/components/layout/flex-row';
import { useBreakpoint } from 'src/hooks/use-breakpoint';
import { VisibilityHandler } from 'src/hooks/use-visible';

export const StyledTemplatePickerModal = styled(LggModal)`
  .ant-modal-content {
    max-width: unset;
    width: 100%;

    .ant-modal-close {
      top: 23px;
      right: 20px;
    }
  }
`;

const ModalHeaderContainer = styled(FlexRow)`
  align-items: center;
  height: 64px;
  padding: 20px 63px 20px 20px;
  width: 740px;
`;

const ModalHeaderTitle = styled.p`
  color: ${({ theme }) => theme.colors.carbon};
  flex: 1;
  font-family: ${({ theme }) => theme.font.regular};
  font-size: 20px;
  letter-spacing: -0.4px;
  line-height: 24px;
  margin: 0;
`;

export const FooterActionButton = styled(LggButton)`
  ${up('md')} {
    font-size: 12px;
  }
`;

export const ModalFooterContainer = styled(FlexRow)`
  align-items: center;
  border-top: 1px solid ${({ theme }) => theme.colors.koala};
  height: 68px;
  justify-content: flex-end;
  padding: 20px 15px;

  button + button {
    margin-left: 20px;
  }

  ${up('md')} {
    height: 64px;

    button + button {
      margin-left: 15px;
    }

    ${FooterActionButton} {
      height: 34px;
      padding: 10px 15px;
      width: max-content;
    }
  }
`;

const HeaderTopContainer = styled.div`
  align-items: center;
  background: ${({ theme }) => theme.colors.alabaster};
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  max-height: 64px;
  padding: 15px 20px;
`;

const HeaderBottomContainer = styled(FlexColumn)`
  align-items: center;
  border-bottom: 1px solid ${({ theme }) => theme.colors.koala};
  height: 64px;
  justify-content: center;
  padding: 15px 20px;
`;

const TemplateCount = styled.p`
  color: ${({ theme }) => theme.colors.smalt};
  flex: 1;
  font-family: ${({ theme }) => theme.font.regular};
  font-size: 15px;
  line-height: 18px;
  margin: 0;
`;

const TemplatePickerBottomDrawer = styled(BottomDrawer)`
  .ant-drawer-body {
    display: flex;
    flex-direction: column;
    height: 100%;
  }
`;

export const ModalBodyContainer = styled.div`
  background: ${({ theme }) => theme.colors.porcelain};
  padding: 20px;
`;

const ModalBodyTopContent = styled(FlexRow)`
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
`;

const baseTemplateListStyles = css`
  height: 100%;
  background-color: ${({ theme }) => theme.colors.white};

  ${up('md')} {
    height: 420px;
    border-radius: 4px;
  }
`;

const TemplateListContainer = styled.div`
  ${baseTemplateListStyles};
  overflow: hidden;
  position: relative;

  &::after {
    background-image: linear-gradient(
      to bottom,
      #fff0,
      ${({ theme }) => theme.colors.white} 88%
    );
    bottom: -1px;
    content: '';
    height: 30px;
    left: 0;
    opacity: 1;
    position: absolute;
    right: 0;
  }
`;

type TemplatePickerModalHeaderProps = {
  title: string;
  headerExtraContent?: React.ReactElement | null;
};

export const TemplatePickerModalHeader = ({
  headerExtraContent = null,
  title,
}: TemplatePickerModalHeaderProps) => {
  return (
    <ModalHeaderContainer>
      <ModalHeaderTitle>{title}</ModalHeaderTitle>
      {headerExtraContent}
    </ModalHeaderContainer>
  );
};

type TemplatePickerModalProps<T> = {
  filtersContent: React.ReactElement;
  headerBottomContent?: React.ReactElement;
  headerExtraContent: React.ReactElement;
  itemsCount: number;
  listContent: React.ReactElement;
  onSubmit: VoidFunction;
  pickerTitle: string;
  selectedTemplate?: T;
  submitButtonLabel?: string;
  testId: string;
  visibilityHandler: VisibilityHandler;
};

export const TemplatePickerModal = <T extends object = any>({
  headerExtraContent,
  itemsCount,
  onSubmit,
  filtersContent,
  visibilityHandler,
  listContent,
  testId,
  selectedTemplate,
  headerBottomContent,
  submitButtonLabel,
  pickerTitle,
}: TemplatePickerModalProps<T>) => {
  const { t } = useTranslation(['conversations', 'common', 'errors']);
  const breakpointUpMd = useBreakpoint(up('md'));

  const templateCount = useMemo(
    () => (
      <TemplateCount data-lgg-id={`${testId}-template-count`}>
        {t('common:template', {
          count: itemsCount ?? 0,
        })}
      </TemplateCount>
    ),
    [testId, itemsCount, t],
  );

  const handleModalClose = useCallback(() => {
    visibilityHandler.close();
  }, [visibilityHandler]);

  const footerContent = useMemo(
    () => (
      <ModalFooterContainer>
        <FooterActionButton
          data-lgg-id={`${testId}-insert-button`}
          variant="primary"
          disabled={!selectedTemplate}
          onClick={() => {
            if (!selectedTemplate) {
              return;
            }

            onSubmit();
          }}
        >
          {submitButtonLabel ?? t('common:insert')}
        </FooterActionButton>
      </ModalFooterContainer>
    ),
    [testId, selectedTemplate, submitButtonLabel, t, onSubmit],
  );

  if (!visibilityHandler.visible) {
    return null;
  }

  return (
    <>
      {breakpointUpMd ? (
        <StyledTemplatePickerModal
          onClose={handleModalClose}
          visible
          width={740}
          maskClosable={false}
          keyboard={false}
          destroyOnClose
          // TODO: Check error with virtuoso and modal animation causing the list to act buggy
          transitionName=""
          maskTransitionName=""
          wrapProps={{
            'data-lgg-id': testId,
          }}
        >
          <>
            <TemplatePickerModalHeader
              title={pickerTitle}
              headerExtraContent={headerExtraContent}
            />
            <ModalBodyContainer>
              <ModalBodyTopContent>
                {templateCount}
                {filtersContent}
              </ModalBodyTopContent>
              <TemplateListContainer>{listContent}</TemplateListContainer>
            </ModalBodyContainer>
            {footerContent}
          </>
        </StyledTemplatePickerModal>
      ) : (
        <TemplatePickerBottomDrawer
          fullHeight
          addScrollbarToBody={false}
          onClose={handleModalClose}
          title={pickerTitle}
          visible={visibilityHandler.visible}
          footer={footerContent}
        >
          <>
            <HeaderTopContainer>
              {templateCount}
              {filtersContent}
            </HeaderTopContainer>
            <HeaderBottomContainer>{headerBottomContent}</HeaderBottomContainer>
            <TemplateListContainer>{listContent}</TemplateListContainer>
          </>
        </TemplatePickerBottomDrawer>
      )}
    </>
  );
};
